/**
 * Chart-series builders for the Leader "Campaigns" tab. Pure functions that turn the delivery /
 * enrollment rollups (`campaignLeader.ts`, `campaignEnrollmentLeader.ts`) into the flat
 * `{ label, value }` rows the tile renderer draws (fed through `CampaignTile` with
 * `DEFAULT_CHART_SETTINGS`). Kept out of the page components so the shaping is testable and shared
 * between the team and individual-contributor views.
 */
import { DELIVERY_STATUSES, type CampaignDelivery, type CampaignEnrollment } from './campaigns';
import { subtreeMetrics, type DeliveryMetrics, type PipelineMetrics } from './campaignLeader';
import {
  subtreeEnrollmentMetrics,
  subtreeEnrollmentMetricsFromCohorts,
  type EnrollmentMetrics,
} from './campaignEnrollmentLeader';
import type { CompletionCohort } from './campaignSalesLevels';
import { getDirectReports, type OrgChartRow } from './orgScope';

export interface ChartPoint {
  label: string;
  value: number;
}

/** The local part of an email, for compact chart-axis labels (`jdoe@cisco.com` → `jdoe`). */
export function shortName(email: string): string {
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

/**
 * Whole-number axis ticks for a count/percent value axis, sized to the largest bar. These series are
 * always integers (delivery counts, completion %), but Nivo's default auto-ticks interpolate
 * fractional values (0, 0.5, 1) on a small range — passing an explicit integer tick array keeps the
 * axis reading in whole numbers. `[0]` when there is no positive value.
 */
export function integerTicks(points: ChartPoint[], maxTicks = 6): number[] {
  const peak = points.reduce((m, p) => (p.value > m ? p.value : m), 0);
  const n = Math.max(0, Math.ceil(peak));
  if (n <= 0) return [0];
  const step = Math.max(1, Math.ceil(n / maxTicks));
  const ticks: number[] = [];
  for (let v = 0; v <= n; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== n) ticks.push(n);
  return ticks;
}

/** Delivery count per delivery status — the delivery-status donut/bar. */
export function deliveryStatusSeries(metrics: DeliveryMetrics): ChartPoint[] {
  return DELIVERY_STATUSES.map((s) => ({ label: s.label, value: metrics.byStatus[s.value] }));
}

/** Pipeline $ per delivery status, rounded to whole dollars — the pipeline-by-status bar. */
export function pipelineStatusSeries(pipeline: Pick<PipelineMetrics, 'byStatus'>): ChartPoint[] {
  return DELIVERY_STATUSES.map((s) => ({
    label: s.label,
    value: Math.round(pipeline.byStatus[s.value]),
  }));
}

/** Enrollment count per status — the "one-time work" completion donut/bar. */
export function enrollmentStatusSeries(metrics: EnrollmentMetrics): ChartPoint[] {
  return [
    { label: 'Not Started', value: metrics.byStatus.not_started },
    { label: 'In Progress', value: metrics.byStatus.in_progress },
    { label: 'Completed', value: metrics.byStatus.completed },
  ];
}

/**
 * One bar per direct report: the subtree delivery count for each — how many deliveries each team
 * member (and their reports) owns. The bar chart of "number of deliveries" across the team.
 */
export function deliveryCountByMember(
  reports: OrgChartRow[],
  orgRows: OrgChartRow[],
  byOwner: Map<string, CampaignDelivery[]>,
): ChartPoint[] {
  return reports.map((r) => {
    const email = String(r.user_email || '').toLowerCase();
    return { label: shortName(email), value: subtreeMetrics(email, orgRows, byOwner).total };
  });
}

/**
 * One bar per direct report: the subtree average enablement completion percent for each — how each
 * team member is progressing through the one-time work. The "progression of teams" chart.
 */
export function enrollmentProgressByMember(
  reports: OrgChartRow[],
  orgRows: OrgChartRow[],
  byUser: Map<string, CampaignEnrollment[]>,
): ChartPoint[] {
  return reports.map((r) => {
    const email = String(r.user_email || '').toLowerCase();
    return { label: shortName(email), value: subtreeEnrollmentMetrics(email, orgRows, byUser).avgPercent };
  });
}

/**
 * One bar per direct report from server cohorts — the scalable peer of {@link enrollmentProgressByMember}.
 * `byEmail` is the per-owner cohort index; each bar re-rolls that report's own sub-subtree.
 */
export function enrollmentProgressByMemberFromCohorts(
  reports: OrgChartRow[],
  orgRows: OrgChartRow[],
  byEmail: Map<string, CompletionCohort>,
): ChartPoint[] {
  return reports.map((r) => {
    const email = String(r.user_email || '').toLowerCase();
    return {
      label: shortName(email),
      value: subtreeEnrollmentMetricsFromCohorts(email, orgRows, byEmail).avgPercent,
    };
  });
}

/** Direct reports of `managerEmail`, memo-free convenience so pages don't re-import `orgScope`. */
export function directReportsOf(orgRows: OrgChartRow[], managerEmail: string): OrgChartRow[] {
  return getDirectReports(orgRows, String(managerEmail || '').toLowerCase());
}
